/**
 * Carries selected files across a navigation, and remembers what the user picked this session.
 *
 * Why this exists: dropping the wrong file type shows ranked "try this instead" links, and
 * 46% of the people who click one never convert anything afterwards — the file was thrown
 * away and they had to start over. See docs/proposed/RETENTION_FIXES.md T1.2.
 *
 * Mechanism (docs/proposed/UX_SPEC_UPLOAD_AND_PREVIEW.md §1.5.1): an IndexedDB record plus
 * an `?xfer=<id>` pointer on the destination URL. IndexedDB is used rather than
 * sessionStorage because it stores a real `File`/`Blob` (no base64 inflation) and is
 * same-origin rather than per-tab, so middle-click and Cmd-click open a new tab and the
 * hand-off still works. Suggestion links therefore stay real `<a href>` elements.
 *
 * The same store backs the session file list (T6.2): the files a user picked recently, offered
 * back to them on other tools so a second job on the same file costs one click instead of a
 * second trip to the file manager. Two separate stores — a hand-off is consumed exactly once
 * and must not be resurrected by the list, and the list must survive a hand-off.
 */

const DB_NAME = 'xconvert-transfer';
const STORE = 'files';
/** Session file list (T6.2). Added in version 2. */
const SESSION_STORE = 'session';
const DB_VERSION = 2;

/** Records older than this are swept on boot. Long enough to survive a slow page load. */
export const TRAY_TTL_MS = 30 * 60 * 1000;
export const TRAY_MAX_FILES = 8;
export const TRAY_MAX_BYTES = 250 * 1024 * 1024;

/**
 * How long a remembered file stays on offer. Longer than a hand-off, which is a single
 * navigation, and short enough that "recently" stays true — this is a session tray, not a file
 * manager. Bounded on purpose: it also caps the privacy blast radius (D6 constraint 2).
 */
export const SESSION_TTL_MS = 4 * 60 * 60 * 1000;
export const SESSION_MAX_FILES = 6;
export const SESSION_MAX_BYTES = 250 * 1024 * 1024;
/**
 * Files larger than this are used but not remembered. Holding a 400 MB video in the user's
 * browser storage to save them one file-picker trip is not a trade worth making for them.
 */
export const SESSION_MAX_FILE_BYTES = 60 * 1024 * 1024;

/**
 * How many files the session list holds, mirrored into localStorage.
 *
 * IndexedDB is async, so a component cannot know at first paint whether it will have rows --
 * which means it renders nothing, then grows, and pushes the page down. Measured on QA that was
 * CLS 0.1022, over Google's 0.1 threshold. localStorage is synchronous, so the count is
 * readable during the first render and the space can be reserved exactly.
 *
 * Only ever a hint: it can disagree with the store (cleared storage, an expired sweep). The
 * component reserves from it and then corrects to whatever the store actually returns, so a
 * stale hint costs a small shift, never a wrong list.
 */
export const SESSION_COUNT_KEY = 'xconvert-session-file-count';

function writeCountHint(n: number): void {
    try {
        if (typeof localStorage === 'undefined') return;
        if (n > 0) localStorage.setItem(SESSION_COUNT_KEY, String(n));
        else localStorage.removeItem(SESSION_COUNT_KEY);
    } catch { /* private mode, quota -- the hint is optional */ }
}

/** Rows the list will probably render, known synchronously. 0 when there is nothing. */
export function sessionCountHint(): number {
    try {
        if (typeof localStorage === 'undefined') return 0;
        const n = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0', 10);
        return Number.isFinite(n) && n > 0 ? Math.min(n, SESSION_MAX_FILES) : 0;
    } catch {
        return 0;
    }
}

export interface TransferRecord {
    xferId: string;
    createdAt: number;
    files: File[];
    fromSlug: string;
    fromTitle: string;
}

/** One remembered file, with where it came from so the UI can say so. */
export interface SessionFileRecord {
    id: string;
    createdAt: number;
    file: File;
    name: string;
    size: number;
    extension: string;
    fromSlug: string;
    fromTitle: string;
}

function openDb(): Promise<IDBDatabase | null> {
    return new Promise(resolve => {
        try {
            if (typeof indexedDB === 'undefined') return resolve(null);
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE, { keyPath: 'xferId' });
                }
                if (!db.objectStoreNames.contains(SESSION_STORE)) {
                    db.createObjectStore(SESSION_STORE, { keyPath: 'id' });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
}

function tx<T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
    storeName: string = STORE,
): Promise<T | null> {
    return openDb().then(db => {
        if (!db) return null;
        return new Promise<T | null>(resolve => {
            try {
                const t = db.transaction(storeName, mode);
                const req = fn(t.objectStore(storeName));
                req.onsuccess = () => resolve(req.result as T);
                req.onerror = () => resolve(null);
                t.oncomplete = () => db.close();
            } catch {
                resolve(null);
            }
        });
    });
}

export class TransferTrayService {
    /**
     * Stash files for a hand-off. Returns the id to put on the destination URL, or null if
     * the tray is unavailable or the payload is too big — callers must treat null as
     * "navigate without the file", never as an error worth blocking on.
     */
    async put(files: File[], fromSlug: string, fromTitle: string): Promise<string | null> {
        if (!files || files.length === 0) return null;
        if (files.length > TRAY_MAX_FILES) return null;
        const bytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
        if (bytes > TRAY_MAX_BYTES) return null;

        const xferId = this.mintId();
        // One record at a time: a new hand-off replaces the old rather than accumulating
        // blobs the user will never pick up.
        await this.clear();
        const record: TransferRecord = { xferId, createdAt: Date.now(), files, fromSlug, fromTitle };
        const ok = await tx('readwrite', store => store.put(record) as IDBRequest<any>);
        return ok === null ? null : xferId;
    }

    /**
     * Read and DELETE a record. Deletion happens before the caller stages anything, so a
     * failure mid-staging can never leave the same bytes to be picked up twice.
     */
    async take(xferId: string): Promise<TransferRecord | null> {
        if (!xferId) return null;
        const rec = await tx<TransferRecord>('readonly', store => store.get(xferId));
        await tx('readwrite', store => store.delete(xferId) as IDBRequest<any>);
        if (!rec || !rec.files || rec.files.length === 0) return null;
        if (Date.now() - (rec.createdAt || 0) > TRAY_TTL_MS) return null;
        return rec;
    }

    /** Drop every hand-off record. Called on boot and before each new hand-off. */
    async clear(): Promise<void> {
        await tx('readwrite', store => store.clear() as IDBRequest<any>);
    }

    /** Boot sweep: remove anything past its TTL so abandoned blobs do not linger. */
    async sweepExpired(): Promise<void> {
        const all = await tx<TransferRecord[]>('readonly', store => store.getAll() as IDBRequest<any>);
        if (all && all.length > 0) {
            const now = Date.now();
            for (const rec of all) {
                if (now - (rec.createdAt || 0) > TRAY_TTL_MS) {
                    await tx('readwrite', store => store.delete(rec.xferId) as IDBRequest<any>);
                }
            }
        }
        await this.sweepSession();
    }

    // ---------------------------------------------------------------- session list (T6.2)

    /**
     * Remember files the user actually used, so other tools can offer them back.
     *
     * Deliberately stores the `File` itself rather than a server handle. That keeps the whole
     * feature client-side — picking one goes through the same selection path as a fresh drop,
     * with no re-registration on the edge — and it sidesteps the constraint that output files
     * are not cross-edge readable yet (D6 constraint 3). It also means a remembered file can
     * never be a dead link: the bytes are local, so there is nothing to existence-check
     * (D6 constraint 6 does not apply to this design).
     *
     * Best-effort throughout. Never throws, never blocks the upload it is riding along with.
     */
    async remember(files: File[], fromSlug: string, fromTitle: string): Promise<void> {
        try {
            if (!files || files.length === 0) return;
            const worth = files.filter(f => f && f.size > 0 && f.size <= SESSION_MAX_FILE_BYTES);
            if (worth.length === 0) return;

            const existing = await this.session();
            const kept: SessionFileRecord[] = [];
            for (const file of worth) {
                // Same name and size means the same file for this purpose; offering it twice
                // is clutter, and the newer entry has the more useful origin label.
                if (kept.some(r => r.name === file.name && r.size === file.size)) continue;
                kept.push({
                    id: this.mintId(),
                    createdAt: Date.now(),
                    file,
                    name: file.name,
                    size: file.size,
                    extension: (file.name.split('.').pop() || '').toLowerCase(),
                    fromSlug,
                    fromTitle,
                });
            }
            for (const rec of existing) {
                if (kept.some(r => r.name === rec.name && r.size === rec.size)) continue;
                kept.push(rec);
            }

            // Newest first, then trim to both caps so storage cannot grow without bound.
            kept.sort((a, b) => b.createdAt - a.createdAt);
            const final: SessionFileRecord[] = [];
            let bytes = 0;
            for (const rec of kept) {
                if (final.length >= SESSION_MAX_FILES) break;
                if (bytes + rec.size > SESSION_MAX_BYTES) continue;
                bytes += rec.size;
                final.push(rec);
            }

            await tx('readwrite', store => store.clear() as IDBRequest<any>, SESSION_STORE);
            for (const rec of final) {
                await tx('readwrite', store => store.put(rec) as IDBRequest<any>, SESSION_STORE);
            }
            writeCountHint(final.length);
        } catch { /* remembering is a convenience; it must never break an upload */ }
    }

    /** Remembered files, newest first, expired ones filtered out. */
    async session(): Promise<SessionFileRecord[]> {
        const all = await tx<SessionFileRecord[]>(
            'readonly', store => store.getAll() as IDBRequest<any>, SESSION_STORE);
        if (!all || all.length === 0) {
            writeCountHint(0);
            return [];
        }
        const now = Date.now();
        const live = all
            .filter(r => r && r.file && now - (r.createdAt || 0) <= SESSION_TTL_MS)
            .sort((a, b) => b.createdAt - a.createdAt);
        // Keep the hint honest, so the next page reserves the right height.
        writeCountHint(live.length);
        return live;
    }

    /** Drop one remembered file — the user's "not this one" control. */
    async forget(id: string): Promise<void> {
        if (!id) return;
        await tx('readwrite', store => store.delete(id) as IDBRequest<any>, SESSION_STORE);
        writeCountHint(Math.max(0, sessionCountHint() - 1));
    }

    /** Drop every remembered file. */
    async forgetAll(): Promise<void> {
        await tx('readwrite', store => store.clear() as IDBRequest<any>, SESSION_STORE);
        writeCountHint(0);
    }

    private async sweepSession(): Promise<void> {
        const all = await tx<SessionFileRecord[]>(
            'readonly', store => store.getAll() as IDBRequest<any>, SESSION_STORE);
        if (!all || all.length === 0) return;
        const now = Date.now();
        for (const rec of all) {
            if (now - (rec.createdAt || 0) > SESSION_TTL_MS) {
                await tx('readwrite', store => store.delete(rec.id) as IDBRequest<any>, SESSION_STORE);
            }
        }
    }

    private mintId(): string {
        try {
            if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }
        } catch { /* fall through */ }
        return `xfer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
}

export const transferTray = new TransferTrayService();
